import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { ButtonV2 } from '@getgrover/ui';

import { CloseButtonWrapper as CloseButtonX } from 'ggApp/modules/newCheckout/components/CheckoutModal/styles';
import { TranslatorComponent_Text as Translator } from 'ggApp/modules/translator';
import flows from 'ggApp/modules/newCheckout/constants/flowNames';
import { dispatchGAEvent } from 'ggApp/modules/stepsFlowProvider/utils';
import stepNames from 'ggApp/modules/newCheckout/constants/stepNames';

import Empty from 'ggApp/shared/components/EmptyCartPage/EmptyCart/images/emptyBox.svg';
import UndoIcon from 'ggApp/shared/icons/Undo';
import CloseSmall from 'ggApp/shared/icons/CloseSmall';

import {
    StyledUndoModal,
    EmptyBagImage,
    UndoTitle,
    UndoText,
    UndoButton,
    CloseButton,
    ContentWrapper,
    ButtonsWrapper,
} from './styles';

const UndoModal = ({ modalRef, confirm, newCheckoutFlag, closeCheckout }) => {
    const [modal, setModal] = useState(null);

    const flowName = useMemo(() => {
        return newCheckoutFlag && newCheckoutFlag.variantKey === 'with-home-address'
            ? flows.CHECKOUT_WITH_HOME_ADDRESS
            : flows.CHECKOUT_NO_HOME_ADDRESS;
    }, [newCheckoutFlag]);

    // send it directly as undo modal is not part of stepper
    const dispatchEvent = useCallback(
        (transitionType) => {
            dispatchGAEvent({
                stepName: stepNames.ORDER_UNDO,
                flowName,
                transitionType,
                eventCategory: 'checkout',
                stepIdentifierMapper: () => 'order_undo',
            });
        },
        [flowName],
    );

    const onOpen = useCallback(() => {
        dispatchEvent('enter');
    }, [dispatchEvent]);

    const createModalRef = useCallback(
        (newModal) => {
            setModal(newModal);
            modalRef(newModal);
        },
        [modalRef],
    );

    const closeUndoModal = useCallback(() => {
        if (modal) {
            modal.closeModal();
        }
    }, [modal]);

    const closeAndClearCart = useCallback(() => {
        confirm();
        dispatchEvent('leave');
        closeCheckout();
        closeUndoModal();
    }, [closeCheckout, closeUndoModal, confirm, dispatchEvent]);

    const handleClickUndo = useCallback(() => {
        closeUndoModal();
        dispatchEvent('cancel');
    }, [closeUndoModal, dispatchEvent]);

    return (
        <div data-testid="undo-modal">
            <StyledUndoModal
                modalRef={createModalRef}
                shouldCloseOnClick={false}
                confirm={confirm}
                onOpen={onOpen}
            >
                <CloseButtonX>
                    <ButtonV2
                        onClick={closeAndClearCart}
                        icon={<CloseSmall />}
                        outlined
                        small
                        data-testid="close-button-x"
                    />
                </CloseButtonX>
                <ContentWrapper>
                    <EmptyBagImage src={Empty} alt="empty" />
                    <UndoTitle>
                        <Translator tk="NEW_CHECKOUT_UNDO_TITLE" />
                    </UndoTitle>
                    <UndoText>
                        <Translator tk="NEW_CHECKOUT_UNDO_TEXT" />
                    </UndoText>
                </ContentWrapper>
                <ButtonsWrapper>
                    <UndoButton
                        icon={<UndoIcon />}
                        fullWidth
                        onClick={handleClickUndo}
                        data-testid="undo-button"
                    >
                        <Translator tk="NEW_CHECKOUT_UNDO_BUTTON" />
                    </UndoButton>
                    <CloseButton fullWidth onClick={closeAndClearCart} data-testid="close-button">
                        <Translator tk="NEW_CHECKOUT_CLOSE_BUTTON" />
                    </CloseButton>
                </ButtonsWrapper>
            </StyledUndoModal>
        </div>
    );
};

UndoModal.propTypes = {
    modalRef: PropTypes.func.isRequired,
    confirm: PropTypes.func,
};

UndoModal.defaultProps = {
    confirm: () => {},
};

export default UndoModal;
